// Reusable feedback views for slow/failable operations (Req 9.4).
//
// Every screen that fetches data or triggers a slow backend job (AI drafting,
// QuickBooks push — Req 9.4) shows the same loading and error states through these
// helpers, so behaviour is consistent app-wide and the feature screens (task 3.6)
// don't each reinvent a spinner or an error panel.
//
// These build DOM (they need a document), so they aren't unit-tested here; the
// routing logic they sit behind is covered by router.test.js.

import { h, mount } from './lib/dom.js';

// Animated spinner + message. `label` reads out for assistive tech via role/status.
export function loadingView(message = 'Loading…') {
  return h('section', { class: 'state state-loading', role: 'status', 'aria-live': 'polite' }, [
    h('span', { class: 'spinner', 'aria-hidden': 'true' }),
    h('p', { class: 'state-msg', text: message }),
  ]);
}

// Error panel with an optional Retry button. `onRetry`, when supplied, is wired to
// a prominent button so a failed slow op (e.g. a QuickBooks push) can be retried
// without leaving the screen.
export function errorView({ title = 'Something went wrong', message, onRetry } = {}) {
  const children = [
    h('p', { class: 'state-title', text: title }),
    message ? h('p', { class: 'state-msg', text: message }) : null,
  ];
  if (typeof onRetry === 'function') {
    children.push(h('button', { class: 'btn btn-secondary', type: 'button', text: 'Try again', onclick: onRetry }));
  }
  return h('section', { class: 'state state-error', role: 'alert' }, children);
}

// Neutral empty state (no records yet), with an optional primary action.
export function emptyView({ message = 'Nothing here yet.', actionLabel, onAction } = {}) {
  const children = [h('p', { class: 'state-msg', text: message })];
  if (actionLabel && typeof onAction === 'function') {
    children.push(h('button', { class: 'btn btn-primary', type: 'button', text: actionLabel, onclick: onAction }));
  }
  return h('section', { class: 'state state-empty' }, children);
}

// Run an async task inside a container with consistent loading -> result / error
// UI (Req 9.4). `task` is a function returning a promise; `render(result)` returns
// the success node. On rejection an error panel with a Retry button (re-runs the
// same task) is shown. Returns a promise that resolves once the container settles.
//
//   runAsync(main, () => api.get('/jobs'), (jobs) => renderJobList(jobs),
//            { loadingMessage: 'Loading jobs…' });
export function runAsync(container, task, render, { loadingMessage } = {}) {
  mount(container, loadingView(loadingMessage));
  return Promise.resolve()
    .then(() => task())
    .then((result) => {
      mount(container, render(result));
    })
    .catch((err) => {
      const message = (err && err.message) || 'Please try again.';
      mount(
        container,
        errorView({
          message,
          onRetry: () => runAsync(container, task, render, { loadingMessage }),
        }),
      );
    });
}
